/**
 * Shared CSV helpers for the finance exports. German-Excel-friendly: ';' as the field separator,
 * a leading UTF-8 BOM at download time, and a comma decimal so Excel reads amounts as numbers.
 *
 * The DATEV/OPOS/EXTF exports predate this file and still carry their own copies of `csvField`
 * (the EXTF format in particular is byte-sensitive, so it is deliberately left untouched). New
 * report exports should use the helpers here instead of adding another copy.
 */

/** Quote a field only when it contains a quote, the ';' separator or a newline; double interior quotes. */
export function csvField(value: string): string {
  return /[";\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** German decimal (comma), two places, no thousands separator or currency symbol — Excel reads it as a number. */
export function csvNumber(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

/** Serialize a header row plus already-formatted string rows as a ';'-separated CSV (CRLF line breaks). */
export function toCsv(header: readonly string[], rows: readonly (readonly string[])[]): string {
  const lines = [header.map(csvField).join(';')];
  for (const row of rows) lines.push(row.map(csvField).join(';'));
  return lines.join('\r\n');
}

/** Trigger a client-side download of CSV text. The leading BOM (as an escape) makes Excel open it as UTF-8. */
export function downloadCsv(content: string, fileName: string): void {
  const blob = new Blob([`﻿${content}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
